// Import styles of packages that you've installed.
// All packages except `@mantine/hooks` require styles imports
import '@mantine/core/styles.css'

import { createTheme, MantineProvider } from '@mantine/core'
import { ErrorBoundary } from 'components/error-boundary/error-boundary'
import { PreviewWarning } from 'components/preview-warning'
import { getProductFromPath, ProductProvider } from 'components/product-context'
import type { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import { appWithTranslation } from 'next-i18next'
import { NuqsAdapter } from 'nuqs/adapters/next/pages'
import nextI18NextConfig from '../next-i18next.config'
import ErrorPage from './_error.page'

const theme = createTheme({
    primaryColor: 'gray',
    colors: {
        // Todoist brand red, built around the design-system ramp (red 500 = #e34432).
        todoist: [
            '#fff6f0',
            '#ffefe5',
            '#fde7d8',
            '#ffc9b3',
            '#ffb399',
            '#ef6a57',
            '#e34432',
            '#cf3520',
            '#a81f00',
            '#7a1700',
        ],
    },
    spacing: {
        xs: '0.5rem',
        sm: '0.75rem',
        md: '1rem',
        lg: '1.5rem',
        xl: '2rem',
    },
})

function App({ Component, pageProps }: AppProps<Record<string, unknown>>) {
    const { pathname } = useRouter()

    const product = getProductFromPath(pathname)
    const isError = 'error' in pageProps
    const isPreview = 'isPreview' in pageProps && pageProps.isPreview
    const isContentfulPreview = 'isContentfulPreview' in pageProps && pageProps.isContentfulPreview

    if (isError) {
        return <ErrorPage {...pageProps} />
    }

    return (
        <NuqsAdapter>
            <ErrorBoundary>
                <MantineProvider theme={theme}>
                    <ProductProvider product={product}>
                        {isPreview && !isContentfulPreview ? <PreviewWarning /> : null}
                        <Component {...pageProps} />
                    </ProductProvider>
                </MantineProvider>
            </ErrorBoundary>
        </NuqsAdapter>
    )
}

export default appWithTranslation(App, nextI18NextConfig)
